import React, { useState, useEffect, useRef } from 'react'
import { Input } from './input'
import { FormatContextMenu } from './FormatContextMenu'
import { Edit2 } from 'lucide-react'
import { ColumnType } from '../../lib/smartFormatter'

interface SmartEditableCellProps {
  value: any
  onUpdate: (value: any) => void
  columnType: ColumnType
  onFormatChange?: (updates: Partial<ColumnType>) => void
  className?: string
  disabled?: boolean
}

export function SmartEditableCell({ 
  value, 
  onUpdate, 
  columnType,
  onFormatChange,
  className = '',
  disabled = false
}: SmartEditableCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [rawValue, setRawValue] = useState(value)
  const [showFormatMenu, setShowFormatMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditValue(value)
    setRawValue(value)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    let processedValue = editValue

    // Process the value based on column type
    switch (columnType.type) {
      case 'currency':
      case 'number':
        // Remove currency symbols and formatting, keep only numbers
        const numStr = String(editValue).replace(/[^\d.-]/g, '')
        const num = parseFloat(numStr)
        processedValue = isNaN(num) ? editValue : num
        break
      
      case 'percentage':
        // Handle percentage input
        let percentStr = String(editValue).replace(/[^\d.-]/g, '')
        let percent = parseFloat(percentStr)
        if (!isNaN(percent)) {
          // If user entered a value > 1, assume it's already a percentage
          // Otherwise, treat as decimal (0.5 = 50%)
          processedValue = percent > 1 ? percent : percent * 100
        } else {
          processedValue = editValue
        }
        break
      
      case 'date':
        // Try to parse as date
        const date = new Date(editValue)
        processedValue = isNaN(date.getTime()) ? editValue : date.toISOString()
        break
      
      default:
        processedValue = editValue
    }

    onUpdate(processedValue)
    setRawValue(processedValue)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(rawValue)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleBlur = () => {
    // Save on blur (clicking outside)
    handleSave()
  }

  const handleClick = () => {
    if (!disabled) {
      // When editing, show the raw value for easier editing
      setEditValue(rawValue)
      setIsEditing(true)
    }
  }

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault()
    
    // Only show context menu for currency and date types
    if (!onFormatChange || (columnType.type !== 'currency' && columnType.type !== 'date')) {
      return
    }
    
    setContextMenuPosition({ x: e.clientX, y: e.clientY })
    setShowFormatMenu(true)
  }

  const getInputType = (): string => {
    switch (columnType.type) {
      case 'currency':
      case 'number':
      case 'percentage':
        return 'number'
      case 'date':
        return 'date'
      case 'email':
        return 'email'
      case 'phone':
        return 'tel'
      default:
        return 'text'
    }
  }

  const getInputProps = () => {
    const props: any = {
      type: getInputType(),
      value: editValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value),
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      className: "h-7 text-sm px-2 py-1 border border-gray-300 focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
    }

    // Add step for number inputs
    if (columnType.type === 'currency' || columnType.type === 'number') {
      props.step = columnType.decimals === 0 ? '1' : '0.01'
    }

    return props
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        {...getInputProps()}
      />
    )
  }

  return (
    <>
      <div 
        className={`flex items-center gap-2 min-h-[28px] px-2 py-1 rounded group ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-blue-50 border border-transparent hover:border-blue-200'
        } ${className}`}
        onClick={handleClick}
        onContextMenu={handleRightClick}
      >
        <span className={`flex-1 text-sm ${getDisplayClass()}`}>
          {getDisplayValue()}
        </span>
        {!disabled && (
          <Edit2 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      
      {showFormatMenu && onFormatChange && (
        <FormatContextMenu
          columnType={columnType}
          onFormatChange={onFormatChange}
          onClose={() => setShowFormatMenu(false)}
          position={contextMenuPosition}
        />
      )}
    </>
  )

  function getDisplayClass(): string {
    switch (columnType.type) {
      case 'currency':
        return 'font-mono text-right'
      case 'number':
        return 'font-mono text-right'
      case 'percentage':
        return 'font-mono text-right'
      case 'email':
        return 'text-blue-600 hover:text-blue-800'
      case 'phone':
        return 'font-mono'
      default:
        return ''
    }
  }

  function getDisplayValue(): string {
    if (value === null || value === undefined || value === '') {
      return ''
    }

    switch (columnType.type) {
      case 'currency':
        return formatCurrency(value)
      case 'percentage':
        return formatPercentage(value)
      case 'number':
        return formatNumber(value)
      case 'date':
        return formatDate(value)
      default:
        return String(value)
    }
  }

  function formatCurrency(val: any): string {
    const num = typeof val === 'number' ? val : parseFloat(val)
    if (isNaN(num)) return String(val)
    
    return new Intl.NumberFormat(columnType.locale || 'en-US', {
      style: 'currency',
      currency: columnType.currency || 'USD',
      minimumFractionDigits: columnType.decimals || 0,
      maximumFractionDigits: columnType.decimals || 2
    }).format(num)
  }

  function formatPercentage(val: any): string {
    const num = typeof val === 'number' ? val : parseFloat(val)
    if (isNaN(num)) return String(val)
    
    // Assume values > 1 are already in percentage form
    const percentValue = num > 1 ? num : num * 100
    
    return new Intl.NumberFormat(columnType.locale || 'en-US', {
      style: 'percent',
      minimumFractionDigits: columnType.decimals || 0,
      maximumFractionDigits: columnType.decimals || 2
    }).format(percentValue / 100)
  }

  function formatNumber(val: any): string {
    const num = typeof val === 'number' ? val : parseFloat(val)
    if (isNaN(num)) return String(val)
    
    return new Intl.NumberFormat(columnType.locale || 'en-US', {
      minimumFractionDigits: columnType.decimals || 0,
      maximumFractionDigits: columnType.decimals || 2
    }).format(num)
  }

  function formatDate(val: any): string {
    // Handle null, undefined, empty values
    if (val === null || val === undefined || val === '') return String(val)
    
    const date = new Date(val)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(val)
    }
    
    // Check if it's the Unix epoch (likely an error)
    if (date.getFullYear() === 1970 && date.getMonth() === 0 && date.getDate() === 1) {
      return String(val)
    }
    
    // Use custom format if specified
    const formatOptions = getDateFormatOptions(columnType.dateFormat || 'short')
    
    return new Intl.DateTimeFormat(columnType.locale || 'en-US', formatOptions).format(date)
  }

  function getDateFormatOptions(format: string): Intl.DateTimeFormatOptions {
    switch (format) {
      case 'long':
        return { year: 'numeric', month: 'long', day: 'numeric' }
      case 'numeric':
        return { year: 'numeric', month: 'numeric', day: 'numeric' }
      case 'iso':
        return { year: 'numeric', month: '2-digit', day: '2-digit' }
      case 'year-month':
        return { year: 'numeric', month: 'short' }
      case 'year-only':
        return { year: 'numeric' }
      case 'short':
      default:
        return { year: 'numeric', month: 'short', day: 'numeric' }
    }
  }
}